/**
 * Investigation panel — AI Investigations UI for the bottom panel right half.
 * Query input, tier selector, RUN button, progress animation, completed list.
 *
 * Work packages: WP-S13.1 (investigation strip), WP-S13.3 (progress animation),
 * WP-S13.5 (query decomposition confirmation), WP-S13.6 (TV panel).
 *
 * Reference: UI-REFERENCE.html dashboard view, ARCHITECTURE.md section 5.
 */

import { eventBus } from '@/core/events';
import { escapeHtml } from '@/utils/sanitize';
import type {
  Investigation,
  InvestigationState,
  InvestigationTierName,
  ScopeContract,
} from '@/intelligence/investigation/types';
import { getCollectionLogSummary } from '@/intelligence/investigation/collection-log';
import type { CollectionLogSummary } from '@/intelligence/investigation/collection-log';
import { addLogEntry, updateLastActivity } from '@/intelligence/investigation/investigation-store';

// ---------------------------------------------------------------------------
// Phase labels for progress checklist
// ---------------------------------------------------------------------------

const PHASE_LABELS: { state: InvestigationState; label: string }[] = [
  { state: 'decompose', label: 'Decomposing query...' },
  { state: 'collect', label: 'Collecting from sources...' },
  { state: 'extract', label: 'Extracting entities...' },
  { state: 'validate', label: 'Validating entities...' },
  { state: 'graph_build', label: 'Building graph...' },
  { state: 'corroborate', label: 'Corroboration pass...' },
  { state: 'synthesize', label: 'Generating assessment...' },
  { state: 'review', label: 'Complete' },
];

// ---------------------------------------------------------------------------
// Completed investigation record (UI-only)
// ---------------------------------------------------------------------------

interface CompletedInvestigation {
  id: string;
  query: string;
  tier: InvestigationTierName;
  nodeCount: number;
  edgeCount: number;
  timestamp: string;
  investigation: Investigation;
}

// ---------------------------------------------------------------------------
// InvestigationPanel class
// ---------------------------------------------------------------------------

export class InvestigationPanel {
  private container: HTMLElement;
  private queryInput: HTMLInputElement | null = null;
  private tierBtns: Map<InvestigationTierName, HTMLButtonElement> = new Map();
  private runBtn: HTMLButtonElement | null = null;
  private progressContainer: HTMLElement | null = null;
  private listContainer: HTMLElement | null = null;
  private emptyState: HTMLElement | null = null;
  private cleanupFns: (() => void)[] = [];

  // State
  private selectedTier: InvestigationTierName = 'STANDARD';
  private isRunning = false;
  private currentInvestigation: Investigation | null = null;
  private completedPhases: Set<InvestigationState> = new Set();
  private currentPhase: InvestigationState | null = null;
  private completedInvestigations: CompletedInvestigation[] = [];
  private entityCount = 0;

  // Decomposition confirmation
  private confirmOverlay: HTMLElement | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'investigation-panel';
    this.container.style.cssText = `
      flex: 1; display: flex; flex-direction: column; overflow: hidden;
    `;

    this.buildUI();
    this.wireEvents();
  }

  get element(): HTMLElement {
    return this.container;
  }

  /** Pre-fill the query input and optionally start the investigation. */
  setQuery(query: string, autoStart = false): void {
    if (this.queryInput) {
      this.queryInput.value = query;
    }
    if (autoStart && !this.isRunning) {
      this.startInvestigation(query);
    }
  }

  destroy(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns.length = 0;
    if (this.confirmOverlay) {
      this.confirmOverlay.remove();
      this.confirmOverlay = null;
    }
  }

  // ---------------------------------------------------------------------------
  // UI construction
  // ---------------------------------------------------------------------------

  private buildUI(): void {
    // Query bar
    const queryBar = document.createElement('div');
    queryBar.style.cssText = `
      padding: 8px 12px; border-bottom: 1px solid var(--border-primary, #1a1f2e);
      flex-shrink: 0;
    `;

    const row = document.createElement('div');
    row.style.cssText = 'display: flex; align-items: center; gap: 6px;';

    // Pulse dot
    const pulseDot = document.createElement('div');
    pulseDot.className = 'inv-pulse-dot';
    pulseDot.style.cssText = `
      width: 6px; height: 6px; border-radius: 50%;
      background: var(--border-primary, #1a1f2e); flex-shrink: 0;
    `;
    row.appendChild(pulseDot);

    // Query input
    this.queryInput = document.createElement('input');
    this.queryInput.type = 'text';
    this.queryInput.placeholder = 'Enter investigation query...';
    this.queryInput.style.cssText = `
      flex: 1; background: var(--bg-primary, #080b12);
      border: 1px solid var(--border-primary, #1a1f2e); border-radius: 3px;
      padding: 5px 10px; font-size: 10px; font-family: inherit;
      color: var(--text-primary, #e2e8f0); outline: none;
    `;
    this.queryInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !this.isRunning) {
        this.startInvestigation(this.queryInput!.value);
      }
    });
    row.appendChild(this.queryInput);

    // Tier selector buttons
    const tierGroup = document.createElement('div');
    tierGroup.style.cssText = 'display: flex; flex-shrink: 0;';

    const tiers: { key: InvestigationTierName; label: string }[] = [
      { key: 'QUICK_SCAN', label: 'QUICK' },
      { key: 'STANDARD', label: 'STD' },
      { key: 'DEEP_DIVE', label: 'DEEP' },
    ];

    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      const btn = document.createElement('button');
      btn.style.cssText = `
        padding: 4px 7px; font-size: 8px; font-family: inherit; font-weight: 600;
        border: 1px solid var(--border-primary, #1a1f2e); cursor: pointer;
        background: ${t.key === this.selectedTier ? 'var(--border-primary, #1a1f2e)' : 'transparent'};
        color: ${t.key === this.selectedTier ? 'var(--text-primary, #e2e8f0)' : 'var(--text-muted, #334155)'};
        border-radius: ${i === 0 ? '3px 0 0 3px' : i === tiers.length - 1 ? '0 3px 3px 0' : '0'};
        ${i > 0 ? 'margin-left: -1px;' : ''}
      `;
      btn.textContent = t.label;
      btn.addEventListener('click', () => {
        if (this.isRunning) return;
        this.selectedTier = t.key;
        this.updateTierButtons();
      });
      tierGroup.appendChild(btn);
      this.tierBtns.set(t.key, btn);
    }
    row.appendChild(tierGroup);

    // RUN button
    this.runBtn = document.createElement('button');
    this.runBtn.style.cssText = `
      padding: 5px 14px; font-size: 9px; font-family: inherit; font-weight: 700;
      letter-spacing: 1px; border: none; border-radius: 3px; cursor: pointer;
      background: #00b4d8; color: #080b12; flex-shrink: 0;
    `;
    this.runBtn.textContent = 'RUN';
    this.runBtn.addEventListener('click', () => {
      if (!this.isRunning && this.queryInput?.value.trim()) {
        this.startInvestigation(this.queryInput.value.trim());
      }
    });
    row.appendChild(this.runBtn);

    queryBar.appendChild(row);
    this.container.appendChild(queryBar);

    // Progress container (hidden initially)
    this.progressContainer = document.createElement('div');
    this.progressContainer.style.cssText = 'display: none;';
    this.container.appendChild(this.progressContainer);

    // List container
    this.listContainer = document.createElement('div');
    this.listContainer.style.cssText = 'flex: 1; overflow-y: auto;';
    this.container.appendChild(this.listContainer);

    // Show empty state
    this.renderEmpty();
  }

  private updateTierButtons(): void {
    for (const [key, btn] of this.tierBtns) {
      const isActive = key === this.selectedTier;
      btn.style.background = isActive ? 'var(--border-primary, #1a1f2e)' : 'transparent';
      btn.style.color = isActive ? 'var(--text-primary, #e2e8f0)' : 'var(--text-muted, #334155)';
    }
  }

  private setRunning(running: boolean): void {
    this.isRunning = running;
    if (this.queryInput) this.queryInput.disabled = running;
    if (this.runBtn) {
      this.runBtn.style.background = running ? 'var(--border-primary, #1a1f2e)' : '#00b4d8';
      this.runBtn.style.color = running ? 'var(--text-muted, #334155)' : '#080b12';
      this.runBtn.style.cursor = running ? 'default' : 'pointer';
    }
    // Update pulse dot
    const dot = this.container.querySelector('.inv-pulse-dot') as HTMLElement | null;
    if (dot) {
      dot.style.background = running ? '#00b4d8' : 'var(--border-primary, #1a1f2e)';
      dot.style.animation = running ? 'inv-pulse 1s infinite' : 'none';
    }
  }

  // ---------------------------------------------------------------------------
  // Progress animation — WP-S13.3
  // ---------------------------------------------------------------------------

  private renderProgress(): void {
    if (!this.progressContainer || !this.currentInvestigation) return;

    this.progressContainer.style.display = 'block';
    this.progressContainer.innerHTML = '';
    this.progressContainer.style.cssText = `
      padding: 10px 12px; border-bottom: 1px solid var(--border-primary, #1a1f2e);
      background: #00b4d806; flex-shrink: 0;
    `;

    // Header: "RUNNING: query" + tier
    const header = document.createElement('div');
    header.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;';

    const queryLabel = document.createElement('span');
    queryLabel.style.cssText = 'font-size: 9px; font-weight: 600; color: #00b4d8;';
    queryLabel.textContent = `RUNNING: "${escapeHtml(this.currentInvestigation.rawQuery)}"`;
    header.appendChild(queryLabel);

    const tierLabel = document.createElement('span');
    tierLabel.style.cssText = 'font-size: 8px; color: var(--text-muted, #334155);';
    tierLabel.textContent = `TIER: ${this.currentInvestigation.tier}`;
    header.appendChild(tierLabel);

    this.progressContainer.appendChild(header);

    // Progress bar
    const progressTrack = document.createElement('div');
    progressTrack.style.cssText = `
      height: 3px; background: var(--border-primary, #1a1f2e);
      border-radius: 2px; overflow: hidden; margin-bottom: 8px;
    `;
    const progressFill = document.createElement('div');
    progressFill.className = 'inv-progress-fill';
    const pct = this.calculateProgressPercent();
    progressFill.style.cssText = `
      height: 100%; width: ${pct}%; background: #00b4d8;
      border-radius: 2px; transition: width 0.5s ease;
    `;
    progressTrack.appendChild(progressFill);
    this.progressContainer.appendChild(progressTrack);

    // Phase checklist
    const phases = document.createElement('div');
    phases.style.cssText = 'display: flex; flex-wrap: wrap; gap: 4px 12px;';

    for (const phase of PHASE_LABELS) {
      const span = document.createElement('span');
      span.style.cssText = 'font-size: 8px; display: flex; align-items: center; gap: 3px;';

      const isCompleted = this.completedPhases.has(phase.state);
      const isCurrent = this.currentPhase === phase.state;

      if (isCompleted) {
        span.style.color = '#30d158';
        span.textContent = `\u2713 ${phase.label}`;
      } else if (isCurrent) {
        span.style.color = '#00b4d8';
        span.textContent = `\u25B8 ${phase.label}`;
      } else {
        span.style.color = 'var(--border-primary, #1a1f2e)';
        span.textContent = `\u25CB ${phase.label}`;
      }

      phases.appendChild(span);
    }
    this.progressContainer.appendChild(phases);

    // Entity count (live)
    if (this.entityCount > 0) {
      const countLabel = document.createElement('div');
      countLabel.style.cssText = 'font-size: 8px; color: #64748b; margin-top: 6px;';
      countLabel.textContent = `Entities extracted: ${this.entityCount}`;
      this.progressContainer.appendChild(countLabel);
    }
  }

  private calculateProgressPercent(): number {
    const allStates: InvestigationState[] = [
      'decompose', 'collect', 'extract', 'validate',
      'graph_build', 'corroborate', 'synthesize', 'review',
    ];
    let completed = 0;
    for (const s of allStates) {
      if (this.completedPhases.has(s)) completed++;
    }
    return Math.round((completed / allStates.length) * 100);
  }

  private hideProgress(): void {
    if (this.progressContainer) {
      this.progressContainer.style.display = 'none';
    }
  }

  /** Show a temporary error message when an investigation fails. */
  private showErrorFeedback(error: string): void {
    if (!this.progressContainer) return;
    this.progressContainer.style.display = 'block';
    this.progressContainer.style.cssText = `
      padding: 10px 12px; border-bottom: 1px solid #ff444444;
      background: #ff444408; flex-shrink: 0;
    `;
    this.progressContainer.innerHTML = '';

    const header = document.createElement('div');
    header.style.cssText = 'font-size: 9px; font-weight: 600; color: #ff4444; margin-bottom: 4px;';
    header.textContent = 'INVESTIGATION FAILED';
    this.progressContainer.appendChild(header);

    const detail = document.createElement('div');
    detail.style.cssText = 'font-size: 8px; color: #ff6666; line-height: 1.4;';
    // Truncate long error messages
    const displayError = error.length > 200 ? error.slice(0, 200) + '...' : error;
    detail.textContent = displayError;
    this.progressContainer.appendChild(detail);

    // Auto-hide after 8 seconds
    setTimeout(() => {
      this.hideProgress();
    }, 8000);
  }

  // ---------------------------------------------------------------------------
  // Completed investigations list
  // ---------------------------------------------------------------------------

  private renderEmpty(): void {
    if (!this.listContainer) return;
    this.listContainer.innerHTML = '';

    if (this.completedInvestigations.length === 0) {
      this.emptyState = document.createElement('div');
      this.emptyState.style.cssText = `
        display: flex; flex-direction: column; align-items: center;
        justify-content: center; height: 100%; color: var(--border-primary, #1a1f2e);
      `;
      const icon = document.createElement('div');
      icon.style.cssText = 'font-size: 24px; margin-bottom: 8px; opacity: 0.4;';
      icon.textContent = '\u25CE';
      this.emptyState.appendChild(icon);

      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 10px; letter-spacing: 1px;';
      msg.textContent = 'NO INVESTIGATIONS YET';
      this.emptyState.appendChild(msg);

      const hint = document.createElement('div');
      hint.style.cssText = 'font-size: 9px; margin-top: 4px;';
      hint.textContent = 'Enter a query above or click INVESTIGATE on a focal point';
      this.emptyState.appendChild(hint);

      this.listContainer.appendChild(this.emptyState);
    } else {
      this.renderCompletedList();
    }
  }

  private renderCompletedList(): void {
    if (!this.listContainer) return;
    this.listContainer.innerHTML = '';

    if (this.completedInvestigations.length === 0) {
      this.renderEmpty();
      return;
    }

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 5px 12px; border-bottom: 1px solid var(--border-primary, #1a1f2e);
    `;
    const headerLabel = document.createElement('span');
    headerLabel.style.cssText = 'font-size: 8px; color: var(--text-muted, #334155); font-weight: 600; letter-spacing: 1px;';
    headerLabel.textContent = 'COMPLETED INVESTIGATIONS';
    header.appendChild(headerLabel);
    this.listContainer.appendChild(header);

    for (const inv of this.completedInvestigations) {
      const card = document.createElement('div');
      card.style.cssText = `
        padding: 10px 12px; border-bottom: 1px solid var(--border-primary, #1a1f2e);
        cursor: pointer; transition: background 0.15s;
      `;
      card.addEventListener('mouseenter', () => { card.style.background = '#1a1f2e33'; });
      card.addEventListener('mouseleave', () => { card.style.background = 'transparent'; });

      // Top row: query + COMPLETE badge
      const topRow = document.createElement('div');
      topRow.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;';

      const queryText = document.createElement('span');
      queryText.style.cssText = 'font-size: 10px; font-weight: 600; color: var(--text-primary, #e2e8f0);';
      queryText.textContent = `"${escapeHtml(inv.query)}"`;
      topRow.appendChild(queryText);

      const badge = document.createElement('span');
      badge.style.cssText = 'font-size: 8px; color: #30d158; font-weight: 600;';
      badge.textContent = 'COMPLETE';
      topRow.appendChild(badge);

      card.appendChild(topRow);

      // Meta row
      const meta = document.createElement('div');
      meta.style.cssText = 'display: flex; gap: 12px; font-size: 8px; color: #475569;';

      const stats = document.createElement('span');
      stats.textContent = `${inv.nodeCount} nodes \u00B7 ${inv.edgeCount} edges`;
      meta.appendChild(stats);

      const tierSpan = document.createElement('span');
      tierSpan.textContent = `Tier: ${inv.tier}`;
      meta.appendChild(tierSpan);

      const timeSpan = document.createElement('span');
      timeSpan.textContent = new Date(inv.timestamp).toLocaleTimeString();
      meta.appendChild(timeSpan);

      card.appendChild(meta);

      // Bottom row: open graph + audit trail toggle
      const bottomRow = document.createElement('div');
      bottomRow.style.cssText = 'display: flex; gap: 12px; margin-top: 4px;';

      const openLink = document.createElement('span');
      openLink.style.cssText = 'font-size: 8px; color: #00b4d8; cursor: pointer;';
      openLink.textContent = 'Open graph \u2192';
      openLink.addEventListener('click', (e) => {
        e.stopPropagation();
        eventBus.emit('investigation:open-graph', inv.investigation);
      });
      bottomRow.appendChild(openLink);

      const auditToggle = document.createElement('span');
      auditToggle.style.cssText = 'font-size: 8px; color: #475569; cursor: pointer;';
      auditToggle.textContent = 'Audit trail \u25BC';
      bottomRow.appendChild(auditToggle);

      card.appendChild(bottomRow);

      // Expandable audit trail section (hidden by default)
      const auditSection = document.createElement('div');
      auditSection.style.cssText = 'display: none; margin-top: 6px; padding-top: 6px; border-top: 1px solid #1e293b;';
      card.appendChild(auditSection);

      let auditLoaded = false;
      auditToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isVisible = auditSection.style.display !== 'none';
        if (isVisible) {
          auditSection.style.display = 'none';
          auditToggle.textContent = 'Audit trail \u25BC';
        } else {
          auditSection.style.display = 'block';
          auditToggle.textContent = 'Audit trail \u25B2';
          if (!auditLoaded) {
            auditLoaded = true;
            this.loadAuditTrail(inv.id, auditSection);
          }
        }
      });

      // Analyst scorecard — 4 dimensions, 1-5 scale
      const scorecardSection = document.createElement('div');
      scorecardSection.style.cssText = 'margin-top: 8px; padding-top: 6px; border-top: 1px solid #1e293b;';

      const scorecardLabel = document.createElement('div');
      scorecardLabel.style.cssText = 'font-size: 8px; color: #f59e0b; letter-spacing: 0.5px; margin-bottom: 6px;';
      scorecardLabel.textContent = 'ANALYST SCORECARD';
      scorecardSection.appendChild(scorecardLabel);

      const dimensions = [
        { id: 'signalToNoise', label: 'Signal/Noise' },
        { id: 'completeness', label: 'Completeness' },
        { id: 'accuracy', label: 'Accuracy' },
        { id: 'actionability', label: 'Actionability' },
      ] as const;
      const scores: Record<string, number> = {};

      for (const dim of dimensions) {
        const row = document.createElement('div');
        row.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-bottom: 3px;';

        const label = document.createElement('span');
        label.style.cssText = 'font-size: 8px; color: #64748b; width: 80px;';
        label.textContent = dim.label;
        row.appendChild(label);

        for (let i = 1; i <= 5; i++) {
          const btn = document.createElement('button');
          btn.style.cssText = `
            width: 16px; height: 16px; border-radius: 2px; border: 1px solid #334155;
            background: transparent; color: #64748b; font-size: 8px; cursor: pointer;
            font-family: 'JetBrains Mono', monospace; padding: 0;
          `;
          btn.textContent = String(i);
          btn.addEventListener('click', () => {
            scores[dim.id] = i;
            // Highlight selected
            const siblings = row.querySelectorAll('button');
            siblings.forEach((s, idx) => {
              (s as HTMLElement).style.background = idx < i ? '#f59e0b33' : 'transparent';
              (s as HTMLElement).style.color = idx < i ? '#f59e0b' : '#64748b';
              (s as HTMLElement).style.borderColor = idx < i ? '#f59e0b' : '#334155';
            });
            // Auto-submit when all 4 dimensions scored
            if (Object.keys(scores).length === 4) {
              this.submitScorecard(inv.id, scores);
            }
          });
          row.appendChild(btn);
        }

        scorecardSection.appendChild(row);
      }

      card.appendChild(scorecardSection);

      this.listContainer.appendChild(card);
    }
  }

  private async submitScorecard(investigationId: string, scores: Record<string, number>): Promise<void> {
    try {
      await addLogEntry({
        investigationId,
        state: 'review',
        action: 'analyst_score',
        detail: scores,
      });
      await updateLastActivity(investigationId);
    } catch (err) {
      console.error('[investigation-panel] Failed to submit scorecard:', err);
    }
  }

  // ---------------------------------------------------------------------------
  // Query decomposition confirmation — WP-S13.5
  // ---------------------------------------------------------------------------

  showDecompositionConfirmation(
    investigation: Investigation,
    scopeContract: ScopeContract,
    onConfirm: (edited: ScopeContract) => void,
    onCancel: () => void,
  ): void {
    // Remove previous overlay if any
    if (this.confirmOverlay) {
      this.confirmOverlay.remove();
    }

    this.confirmOverlay = document.createElement('div');
    this.confirmOverlay.style.cssText = `
      position: fixed; inset: 0; z-index: 9999;
      background: rgba(0, 0, 0, 0.7); display: flex;
      align-items: center; justify-content: center;
      font-family: 'JetBrains Mono', monospace;
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
      background: #0e1219; border: 1px solid #1e293b; border-radius: 6px;
      width: 560px; max-height: 80vh; overflow-y: auto; padding: 20px;
      color: #e2e8f0;
    `;

    // Title
    const title = document.createElement('div');
    title.style.cssText = 'font-size: 12px; font-weight: 600; letter-spacing: 1px; margin-bottom: 16px; color: #00b4d8;';
    title.textContent = 'CONFIRM INVESTIGATION SCOPE';
    modal.appendChild(title);

    // Query display
    const queryRow = document.createElement('div');
    queryRow.style.cssText = 'margin-bottom: 14px;';
    const queryLabel = document.createElement('div');
    queryLabel.style.cssText = 'font-size: 8px; color: #334155; letter-spacing: 1px; margin-bottom: 3px;';
    queryLabel.textContent = 'QUERY';
    queryRow.appendChild(queryLabel);
    const queryVal = document.createElement('div');
    queryVal.style.cssText = 'font-size: 10px; color: #e2e8f0;';
    queryVal.textContent = investigation.rawQuery;
    queryRow.appendChild(queryVal);
    modal.appendChild(queryRow);

    // Editable fields using the ScopeContract
    const editState = { ...scopeContract };

    // Subject
    const subjectField = this.buildField('SUBJECT', scopeContract.subject.description);
    modal.appendChild(subjectField.wrapper);

    // Behavior Indicators
    const indicatorsText = scopeContract.behaviorIndicators
      .map(bi => `${bi.type}: ${bi.threshold}`)
      .join('\n');
    const indicatorsField = this.buildField('BEHAVIOR INDICATORS', indicatorsText, true);
    modal.appendChild(indicatorsField.wrapper);

    // Geographic Bounds
    const geoText = `${scopeContract.geographicBounds.source}\nBBox: [${scopeContract.geographicBounds.bbox.join(', ')}]`;
    const geoField = this.buildField('GEOGRAPHIC BOUNDS', geoText);
    modal.appendChild(geoField.wrapper);

    // Temporal Bounds
    const temporalText = `${scopeContract.temporalBounds.start} to ${scopeContract.temporalBounds.end}\n(${scopeContract.temporalBounds.source})`;
    const temporalField = this.buildField('TEMPORAL BOUNDS', temporalText);
    modal.appendChild(temporalField.wrapper);

    // Exclusions
    const exclusionsText = scopeContract.exclusions.join('\n');
    const exclusionsField = this.buildField('EXCLUSIONS', exclusionsText, true);
    modal.appendChild(exclusionsField.wrapper);

    // Buttons
    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display: flex; gap: 8px; margin-top: 16px; justify-content: flex-end;';

    const cancelBtn = document.createElement('button');
    cancelBtn.style.cssText = `
      padding: 7px 16px; font-size: 10px; font-family: inherit; font-weight: 600;
      border: 1px solid #1e293b; border-radius: 3px; background: transparent;
      color: #64748b; cursor: pointer; letter-spacing: 0.5px;
    `;
    cancelBtn.textContent = 'CANCEL';
    cancelBtn.addEventListener('click', () => {
      this.confirmOverlay?.remove();
      this.confirmOverlay = null;
      onCancel();
    });
    btnRow.appendChild(cancelBtn);

    const confirmBtn = document.createElement('button');
    confirmBtn.style.cssText = `
      padding: 7px 16px; font-size: 10px; font-family: inherit; font-weight: 700;
      border: none; border-radius: 3px; background: #00b4d8;
      color: #080b12; cursor: pointer; letter-spacing: 1px;
    `;
    confirmBtn.textContent = 'CONFIRM & COLLECT';
    confirmBtn.addEventListener('click', () => {
      // Read edited values back into scope contract
      const edited: ScopeContract = {
        ...editState,
        subject: {
          ...editState.subject,
          description: subjectField.getValue(),
        },
        exclusions: exclusionsField.getValue().split('\n').filter(l => l.trim()),
      };
      this.confirmOverlay?.remove();
      this.confirmOverlay = null;
      onConfirm(edited);
    });
    btnRow.appendChild(confirmBtn);

    modal.appendChild(btnRow);
    this.confirmOverlay.appendChild(modal);

    // Close on overlay click
    this.confirmOverlay.addEventListener('click', (e) => {
      if (e.target === this.confirmOverlay) {
        this.confirmOverlay?.remove();
        this.confirmOverlay = null;
        onCancel();
      }
    });

    document.body.appendChild(this.confirmOverlay);
  }

  private buildField(label: string, value: string, multiline = false): {
    wrapper: HTMLElement;
    getValue: () => string;
  } {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'margin-bottom: 12px;';

    const lbl = document.createElement('div');
    lbl.style.cssText = 'font-size: 8px; color: #334155; letter-spacing: 1px; margin-bottom: 4px;';
    lbl.textContent = label;
    wrapper.appendChild(lbl);

    if (multiline) {
      const textarea = document.createElement('textarea');
      textarea.style.cssText = `
        width: 100%; min-height: 60px; background: #080b12;
        border: 1px solid #1e293b; border-radius: 3px; padding: 6px 8px;
        font-size: 9px; font-family: inherit; color: #e2e8f0;
        outline: none; resize: vertical; box-sizing: border-box;
      `;
      textarea.value = value;
      wrapper.appendChild(textarea);
      return { wrapper, getValue: () => textarea.value };
    }

    const input = document.createElement('input');
    input.type = 'text';
    input.style.cssText = `
      width: 100%; background: #080b12;
      border: 1px solid #1e293b; border-radius: 3px; padding: 6px 8px;
      font-size: 9px; font-family: inherit; color: #e2e8f0;
      outline: none; box-sizing: border-box;
    `;
    input.value = value;
    wrapper.appendChild(input);
    return { wrapper, getValue: () => input.value };
  }

  // ---------------------------------------------------------------------------
  // Audit trail viewer — WP-S15.3
  // ---------------------------------------------------------------------------

  private async loadAuditTrail(investigationId: string, container: HTMLElement): Promise<void> {
    container.innerHTML = '<div style="font-size: 8px; color: #334155;">Loading audit trail...</div>';

    try {
      const summary = await getCollectionLogSummary(investigationId);
      this.renderAuditTrail(summary, container);
    } catch (err) {
      container.innerHTML = '<div style="font-size: 8px; color: #ff4444;">Failed to load audit trail</div>';
    }
  }

  private renderAuditTrail(summary: CollectionLogSummary, container: HTMLElement): void {
    container.innerHTML = '';

    const style = 'font-size: 8px; color: #64748b; line-height: 1.6;';

    // Stats row
    const stats = document.createElement('div');
    stats.style.cssText = style;
    stats.innerHTML = [
      `Tasks: ${summary.tasksCompleted}/${summary.tasksDispatched} completed`,
      summary.tasksFailed > 0 ? ` (<span style="color:#ff4444">${summary.tasksFailed} failed</span>)` : '',
      ` &middot; Entities: ${summary.entitiesExtracted} extracted`,
      summary.entitiesRejected > 0 ? ` (<span style="color:#ffaa00">${summary.entitiesRejected} rejected</span>)` : '',
      ` &middot; LLM calls: ${summary.llmCalls.length}`,
    ].join('');
    container.appendChild(stats);

    // Phase checklist
    const phaseList = document.createElement('div');
    phaseList.style.cssText = 'margin-top: 4px;';

    for (const phase of summary.phases) {
      if (phase.phase === 'error') continue;
      const row = document.createElement('div');
      row.style.cssText = `font-size: 8px; color: ${phase.completed ? '#30d158' : '#1e293b'}; padding: 1px 0;`;
      const icon = phase.completed ? '\u2713' : '\u25CB';
      row.textContent = `${icon} ${phase.label}`;
      if (phase.timestamp) {
        const time = document.createElement('span');
        time.style.cssText = 'color: #334155; margin-left: 6px;';
        time.textContent = new Date(phase.timestamp).toLocaleTimeString();
        row.appendChild(time);
      }
      phaseList.appendChild(row);
    }
    container.appendChild(phaseList);

    // Synthesis info
    if (summary.synthesis) {
      const synthInfo = document.createElement('div');
      synthInfo.style.cssText = 'margin-top: 4px; font-size: 8px; color: #00b4d8;';
      synthInfo.textContent = `Assessment: ${summary.synthesis.sectionCount} sections, ${summary.synthesis.tokensUsed} tokens (${summary.synthesis.model})`;
      container.appendChild(synthInfo);
    }

    // LLM call summary
    if (summary.llmCalls.length > 0) {
      const llmHeader = document.createElement('div');
      llmHeader.style.cssText = 'margin-top: 4px; font-size: 7px; color: #334155; letter-spacing: 1px;';
      llmHeader.textContent = 'LLM CALLS';
      container.appendChild(llmHeader);

      const totalTokens = summary.llmCalls.reduce((sum, c) => sum + c.tokensUsed, 0);
      const tokenLine = document.createElement('div');
      tokenLine.style.cssText = 'font-size: 8px; color: #64748b;';
      tokenLine.textContent = `Total: ${totalTokens} tokens across ${summary.llmCalls.length} calls`;
      container.appendChild(tokenLine);
    }

    // Errors
    if (summary.errors.length > 0) {
      const errHeader = document.createElement('div');
      errHeader.style.cssText = 'margin-top: 4px; font-size: 7px; color: #ff4444; letter-spacing: 1px;';
      errHeader.textContent = 'ERRORS';
      container.appendChild(errHeader);

      for (const err of summary.errors.slice(0, 5)) {
        const errLine = document.createElement('div');
        errLine.style.cssText = 'font-size: 8px; color: #ff6666;';
        errLine.textContent = `[${err.state}] ${err.error}`;
        container.appendChild(errLine);
      }
      if (summary.errors.length > 5) {
        const more = document.createElement('div');
        more.style.cssText = 'font-size: 8px; color: #475569;';
        more.textContent = `... and ${summary.errors.length - 5} more`;
        container.appendChild(more);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------------------

  private wireEvents(): void {
    // Investigation state transitions
    this.cleanupFns.push(
      eventBus.on('investigation:state', ({ investigationId, from, to }) => {
        if (this.currentInvestigation?.id !== investigationId) return;
        this.completedPhases.add(from);
        this.currentPhase = to;
        this.renderProgress();
      }),
    );

    // Investigation completed
    this.cleanupFns.push(
      eventBus.on('investigation:complete', (inv) => {
        if (this.currentInvestigation?.id !== inv.id) return;
        this.isRunning = false;
        this.setRunning(false);
        this.completedPhases.add('review');
        this.currentPhase = null;

        // Add to completed list
        this.completedInvestigations.unshift({
          id: inv.id,
          query: inv.rawQuery,
          tier: inv.tier,
          nodeCount: inv.nodeCount,
          edgeCount: inv.edgeCount,
          timestamp: inv.completedAt ?? new Date().toISOString(),
          investigation: inv,
        });

        this.hideProgress();
        this.renderCompletedList();
        this.currentInvestigation = null;
        this.entityCount = 0;
      }),
    );

    // Investigation error
    this.cleanupFns.push(
      eventBus.on('investigation:error', ({ investigationId, error }) => {
        // Match if this is the current investigation OR if we're in a loading
        // state with no investigation assigned yet (pre-start failures)
        const isOurs = this.currentInvestigation?.id === investigationId;
        const isOrphanedLoading = !this.currentInvestigation && this.isRunning;
        if (!isOurs && !isOrphanedLoading) return;
        this.isRunning = false;
        this.setRunning(false);
        this.hideProgress();
        this.showErrorFeedback(error);
        this.currentInvestigation = null;
        this.entityCount = 0;
      }),
    );

    // Entity extracted — increment counter
    this.cleanupFns.push(
      eventBus.on('investigation:entity-extracted', ({ investigationId }) => {
        if (this.currentInvestigation?.id !== investigationId) return;
        this.entityCount++;
        this.renderProgress();
      }),
    );

    // Investigation start (from orchestrator)
    this.cleanupFns.push(
      eventBus.on('investigation:start', (inv) => {
        this.currentInvestigation = inv;
        this.isRunning = true;
        this.completedPhases.clear();
        this.currentPhase = inv.state;
        this.entityCount = 0;
        this.setRunning(true);
        this.renderProgress();
      }),
    );

    // External request to investigate (from focal points, news, entity panel)
    // Only update the UI — the orchestrator handles starting the investigation
    this.cleanupFns.push(
      eventBus.on('investigation:request', ({ query, tier }) => {
        this.selectedTier = tier;
        this.updateTierButtons();
        if (this.queryInput) {
          this.queryInput.value = query;
        }
        // Do NOT call startInvestigation here — that would re-emit the event.
        // The orchestrator listens to investigation:request and handles the start.
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Start investigation — emit event for orchestrator to handle
  // ---------------------------------------------------------------------------

  private startInvestigation(query: string): void {
    if (!query.trim() || this.isRunning) return;

    // Show immediate loading state before async orchestrator responds
    this.setRunning(true);

    eventBus.emit('investigation:request', { query: query.trim(), tier: this.selectedTier });
  }
}

// ---------------------------------------------------------------------------
// CSS keyframes injection
// ---------------------------------------------------------------------------

let _keyframesInjected = false;

function injectKeyframes(): void {
  if (_keyframesInjected) return;
  _keyframesInjected = true;

  const style = document.createElement('style');
  style.id = 'investigation-panel-keyframes';
  style.textContent = `
    @keyframes inv-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

let _instance: InvestigationPanel | null = null;

/** Get or create the investigation panel singleton. */
export function getInvestigationPanel(): InvestigationPanel {
  if (!_instance) {
    injectKeyframes();
    _instance = new InvestigationPanel();
  }
  return _instance;
}

/** Create the investigation panel element for mounting. */
export function createInvestigationPanel(): HTMLElement {
  return getInvestigationPanel().element;
}

/** Destroy the investigation panel. */
export function destroyInvestigationPanel(): void {
  if (_instance) {
    _instance.destroy();
    _instance = null;
  }
}
